import {useMutation} from '@tanstack/react-query'
import {Error, Success} from 'src/feature/bite/helpers/notification'
import useRequestParams from 'src/feature/bite/hook/request'
import {useAuthUser} from 'react-auth-kit'
import {useReferral} from '../hook'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import {CAMPAIGN_API} from 'src/feature/bite/domain'
import {useIntl} from 'react-intl'

const apiProgram = `${CAMPAIGN_API}/ReferralProgram`
const apiProduct = `${CAMPAIGN_API}/Product`
const apiCampaignProduct = `${CAMPAIGN_API}/CampaignProduct`
const apiExcludeCampaignProduct = `${CAMPAIGN_API}/CampaignExcludeProduct`
const apiUrlRedeem = `${CAMPAIGN_API}/Campaign`
const apiPriceCluster = `${CAMPAIGN_API}/CampaignPriceCluster`
const apiCampaign = `${CAMPAIGN_API}/CampaignPoint`
const apiCampaignFile = `${CAMPAIGN_API}/CampaignFile`

const apiFile = `${CAMPAIGN_API}/File`

export function useUpdateReferralProgram() {
  const auth = useAuthUser()
  const {headers} = useRequestParams()
  const intl = useIntl()

  return useMutation(
    ['ReferralProgramUpdate'],
    async ({values}: {values: any}) => {
      const response = await updateReferral(auth().id, headers, values)

      if (!response.result.isSuccess) {
        Error(response?.result?.errorMessage)
        return false
      }

      if (values.status === 1) {
        Success(intl.formatMessage({id: 'UPDATE.REFERRAL.PROGRAM.ACTIVE'}))
      } else {
        Success(intl.formatMessage({id: 'UPDATE.REFERRAL.PROGRAM.INACTIVE'}))
      }

      return true
    },
    {
      onSuccess: () => {
        // Success(intl.formatMessage({id: 'UPDATE.REFERRAL.PROGRAM.ACTIVE'}))
      },
      onError: () => {
        // Error(intl.formatMessage({id: 'UPDATE.REFERRAL.PROGRAM.INACTIVE'}))
      },
    }
  )
}

export function useCreateReferralCampaign() {
  const auth = useAuthUser()
  const {headers, errors} = useRequestParams()

  const timeZoneOffSet = getTimeZone()

  return useMutation(
    ['CreateReferralCampaign'],
    async ({
      values,
      products,
      type,
      program,
      uploadFileIdsState,
    }: {
      values: any
      products: string[]
      type: 'referringCustomerRewardID' | 'referredCustomerRewardID'
      program: any
      uploadFileIdsState
    }) => {
      const reqBody = {
        userID: auth().id,
        language: headers.Language,
        campaign: {
          name: values.title,
          description: values.description,
          status: program?.status,
          timeZoneOffSet,
          startDate: '2024-05-20T16:40:39.265Z',
          endDate: '2100-05-02T13:54:48.978Z',
          workingDays: [0, 1, 2, 3, 4, 5, 6, 7],
          workingStartTime: '00:00:00',
          workingEndTime: '23:59:59',
          promoCluster: values.promoCluster,
          promoType: values.promoType,
          promoGroup: values.promoGroup,
          priceStrategy: 0,
          isRepeatAction: true,
          minAmountEqualValue: 0,
          discountGroup: 0,
          maxBenefitAmount: 0,
          amountLimit: 0,
          countLimit: 0,
          order: 0,
          sub: 0,
          super: 0,
          maxOfBenefit: 0,
          periodType: 0,
          workerPeriodType: 0,
          periodValue: 0,
          workerPeriodValue: 0,
          minOfDailyTX: 0,
          maxOfDailyTX: 0,
          maxOfBenefitAllowedPerDay: 0,
          maxOfBenefitAllowedPerTX: 0,
          maxOfTrxAllowed: 0,
          amount: 0,
          amountCalculateType: 0,
          periodBenefit: 0,
          basketCount: 0,
          isDifferentDays: true,
          totalBenefitAmount: 0,
          totalRemainingBenefitAmount: 0,
        },
      }

      const Response = await fetch(`${apiUrlRedeem}/CreateCampaign`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reqBody),
      })

      const Data = await Response.json()

      const campaignID = Data?.campaignID

      await Promise.all([
        await createCampaignPoint(auth().id, headers, campaignID, Number(values.fixedValue)),
        await createCampaignPriceCluster(auth().id, headers, campaignID, values.priceCluster),
        await updateReferral(auth().id, headers, {
          ...program,
          [type]: campaignID,
        }),
      ])

      if (values.productInSelectionType === '1') {
        await createCampaignProduct(auth().id, headers, campaignID, ['All'])
      }
      if (values.productInSelectionType === '2') {
        await createCampaignProduct(auth().id, headers, campaignID, products)
      }

      if (values.productInSelectionType === '3') {
        await Promise.all([
          await createCampaignFile(auth().id, headers, campaignID, uploadFileIdsState),
        ])
      }

      if (values.productOutSelectionType === '3') {
        await Promise.all([
          await createCampaignProduct(auth().id, headers, campaignID, ['All']),
          await createCampaignFile(auth().id, headers, campaignID, uploadFileIdsState),
        ])
      }

      return true
    },
    {
      onSuccess: () => {
        Success(errors.referralProgram.create_success)
      },
      onError: (error) => {
        Error(errors.referralProgram.create_error)
      },
    }
  )
}

export function useUpdateReferralCampaign() {
  const auth = useAuthUser()
  const {headers, errors} = useRequestParams()

  return useMutation(
    ['UpdateReferralCampaign'],
    async ({
      values,
      campaignID,
      product,
      uploadFileIdsState,
      deleteFileIdsState,
    }: {
      values: any
      campaignID: string
      product: {
        removedProductsId: string[]
        addedProductsId: string[]
      }
      uploadFileIdsState: {name: string; id: string}[]
      deleteFileIdsState: string[]
    }) => {
      if (values.productInSelectionType === '1') {
        await Promise.all([
          await createCampaignProduct(auth().id, headers, campaignID, ['All']),
          await deleteExcludeCampaignProduct(auth().id, headers, campaignID, ['All']),
        ])
      }

      if (values.productInSelectionType === '2') {
        await Promise.all([
          await deleteCampaignProduct(auth().id, headers, campaignID, [
            'All',
            ...product.removedProductsId,
          ]),
          await createCampaignProduct(auth().id, headers, campaignID, product.addedProductsId),
          await deleteExcludeCampaignProduct(auth().id, headers, campaignID, ['All']),
        ])
      }

      if (values.productInSelectionType === '3') {
        await Promise.all([
          await deleteCampaignProduct(auth().id, headers, campaignID, ['All']),
          // await createCampaignProduct(auth().id, headers, campaignID, ['All']),
          await createCampaignFile(auth().id, headers, campaignID, uploadFileIdsState),
        ])
      }

      if (values.productOutSelectionType === '3') {
        await Promise.all([
          await createCampaignProduct(auth().id, headers, campaignID, ['All']),
          await createCampaignFile(auth().id, headers, campaignID, uploadFileIdsState),
        ])
      }

      if (deleteFileIdsState.length > 0) {
        await deleteCampaignFile(auth().id, headers, campaignID, deleteFileIdsState)
      }
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.referralProgram.update_error)
      },
    }
  )
}

export function useAddProductReferralCampaign() {
  const auth = useAuthUser()
  const {headers, errors} = useRequestParams()

  return useMutation(
    ['AddProductReferralCampaign'],
    async ({productCodes, campaignID}: {productCodes: string[]; campaignID: string}) => {
      const reqBody = {
        userID: auth().id,
        productCodes,
        campaignID,
        option: false,
        type: 1,
        filterRequest: {
          filterList: [],
          orderList: [],
          paging: {
            currentPageNumber: 1,
            pageSize: 10,
          },
        },
      }
      const Response = await fetch(`${apiProduct}/CreateOrUpdate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reqBody),
      })

      const Data = await Response.json()

      if (!Data?.responseResult?.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.referralProgram.create_error)
        return false
      }

      Success(errors.referralProgram.create_success)

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.referralProgram.create_error)
      },
    }
  )
}

export function useDeleteRedeem() {
  const auth = useAuthUser()
  const {headers, errors} = useRequestParams()

  return useMutation(
    ['DeleteRedeem'],
    async ({redeemID, program}: {redeemID: string; program: any}) => {
      await fetch(`${apiUrlRedeem}/UpdateCampaignStatus`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: auth().id,
          campaignID: redeemID,
          status: 3,
        }),
      })

      await updateReferral(auth().id, headers, {
        ...program,
        status: 2,
      })

      return true
    },
    {
      onSuccess: () => {
        Success(errors.referralProgram.delete_success)
      },
      onError: (error) => {
        Error(errors.referralProgram.delete_error)
      },
    }
  )
}

export function useUploadFile() {
  const auth = useAuthUser()
  const {headers} = useRequestParams()
  const {uploadFileIdsState, uploadFileIdsSet, uploadProductsSet} = useReferral()

  return useMutation(
    ['UploadFile'],
    async ({file, type}: {file: any; type: number}) => {
      const formData = new FormData()
      formData.append('userID', auth().id)
      formData.append('file', file)
      formData.append('type', type.toString())

      const Response = await fetch(`${apiFile}/UploadFile`, {
        method: 'POST',
        headers: {Authorization: headers.Authorization},
        body: formData,
      })

      const Data = await Response.json()

      const ResponseProduct = await fetch(`${apiCampaignProduct}/GetAllCampaignProduct`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: auth().id,
          campaignID: '',
          fileIDList: [Data?.fileID],
          paging: {
            currentPageNumber: 1,
            pageSize: 100,
          },
        }),
      })

      const DataProduct = await ResponseProduct.json()

      uploadFileIdsSet([
        ...uploadFileIdsState,
        {
          name: file.name,
          id: Data?.fileID,
          type,
        },
      ])

      const productsAddType = DataProduct.data.map((product) => {
        return {
          ...product,
          type,
        }
      })

      uploadProductsSet(productsAddType)

      return Data
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        // Error(errors.referralProgram.upload_error)
      },
    }
  )
}

export const createCampaignPoint = async (
  userID: string,
  headers: any,
  campaignID: string,
  fixedValue: number
) => {
  const campaignPoint = {
    accrualPercentage: 0,
    fixedValue,
    redemptionValue: 1,
    activationType: 1,
    deferredTime: 'string',
    everyAfterAccrualMonth: [0],
    everyAfterAccrualWeek: [0],
    everyAfterAccrualDay: [0],
    everyAfterAccrualTime: 'string',
    expirationType: 0,
    expirationValue: 0,
    expirationTime: '23:99',
    expirationDate: '2100-05-21T13:25:23.237Z',
    pointsToBeEarned: 0,
    amountToBeSpent: 0,
    multiplier: 0,
  }

  const reqBodyPoint = {
    userID,
    language: headers.Language,
    campaignID,
    campaignPoint,
  }

  const ResponsePoint = await fetch(`${apiCampaign}/CreateCampaignPoint`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyPoint),
  })

  const DataPoint = await ResponsePoint.json()

  return DataPoint
}

export const createCampaignPriceCluster = async (
  userID: string,
  headers: any,
  campaignID: string,
  priceCluster: any
) => {
  if (priceCluster.length === 0) {
    return
  }

  const reqBodyPriceCluster = {
    userID: userID,
    language: headers.Language,
    campaignID,
    campaignPriceClusterList: priceCluster,
  }

  const ResponsePriceCluster = await fetch(`${apiPriceCluster}/CreateCampaignPriceCluster`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyPriceCluster),
  })

  const DataPriceCluster = await ResponsePriceCluster.json()

  return DataPriceCluster
}

export const createCampaignProduct = async (
  userID: string,
  headers: any,
  campaignID: string,
  productIDList: string[]
) => {
  if (productIDList.length === 0) {
    return
  }

  const reqBodyProduct = {
    userID,
    campaignID,
    productIDList,
    language: headers.Language,
  }

  const ResponseProduct = await fetch(`${apiCampaignProduct}/CreateCampaignProduct`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyProduct),
  })

  const DataProduct = await ResponseProduct.json()

  return DataProduct
}

export const createCampaignFile = async (
  userID: string,
  headers: any,
  campaignID: string,
  fileIDList: {id: string; name: string}[]
) => {
  if (fileIDList.length === 0) {
    return
  }

  const ids = fileIDList.map((file) => file.id)

  const reqBodyFile = {
    userID,
    campaignID,
    fileIDList: ids,
    language: headers.Language,
  }

  const ResponseFile = await fetch(`${apiCampaignFile}/CreateCampaignFile`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyFile),
  })

  const DataFile = await ResponseFile.json()

  return DataFile
}

export const deleteCampaignFile = async (
  userID: string,
  headers: any,
  campaignID: string,
  fileIDList: string[]
) => {
  if (fileIDList.length === 0) {
    return
  }

  const reqBodyFile = {
    userID,
    campaignID,
    fileIDList,
    language: headers.Language,
  }

  const ResponseFile = await fetch(`${apiCampaignFile}/DeleteCampaignFile`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyFile),
  })

  const DataFile = await ResponseFile.json()

  return DataFile
}

export const deleteCampaignProduct = async (
  userID: string,
  headers: any,
  campaignID: string,
  productIDList: string[]
) => {
  if (productIDList.length === 0) {
    return
  }

  const reqBodyProduct = {
    userID,
    campaignID,
    productIDList,
    language: headers.Language,
  }

  const ResponseProduct = await fetch(`${apiCampaignProduct}/DeleteCampaignProduct`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyProduct),
  })

  const DataProduct = await ResponseProduct.json()

  return DataProduct
}

export const deleteExcludeCampaignProduct = async (
  userID: string,
  headers: any,
  campaignID: string,
  productIDList: string[]
) => {
  if (productIDList.length === 0) {
    return
  }

  const reqBodyProduct = {
    userID,
    campaignID,
    productIDList,
    language: headers.Language,
  }

  const ResponseProduct = await fetch(`${apiExcludeCampaignProduct}/DeleteCampaignExcludeProduct`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyProduct),
  })

  const DataProduct = await ResponseProduct.json()

  return DataProduct
}

export const updateReferral = async (userID: string, headers: any, referralProgram: any) => {
  const reqBodyReferral = {
    userID,
    referralProgram,
    language: headers.Language,
  }

  const ResponseReferral = await fetch(`${apiProgram}/UpdateReferralProgram`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyReferral),
  })

  const DataReferral = await ResponseReferral.json()

  return DataReferral
}
